import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Flyout from '@pushpay/flyout';
import { ArrowSolidIcon, ArrowSingleIcon } from '@pushpay/iconography';
import { clsx, createUseStyles } from '@pushpay/styles';
import { useMediaBreakpoint } from '@pushpay/theming';
import { forwardRef } from '@pushpay/types';
import { FocusLock, useForwardedRef, useLockBodyScroll, useOnClickOutside } from '@pushpay/utils';
const useStyles = createUseStyles(theme => ({
  root: {
    position: 'relative',
    userSelect: 'none',
    outline: 'none'
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    outline: 'none'
  },
  showOutline: theme.OUTLINE.INTERACTIVE_ELEMENT_FOCUS_VISIBLE,
  summaryActive: {},
  overlay: {},
  mobileOverlay: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.colors['color-background-surface'],
    zIndex: theme.ELEVATION.MODAL.Z_INDEX
  },
  arrow: ({
    disabled
  }) => ({
    fontSize: theme.SPACING.MEDIUM_ICON_SIZE,
    margin: `0 ${theme.SPACING.ICON_TO_TEXT}`,
    color: disabled ? theme.colors['color-text-disabled'] : 'inherit',
    flexShrink: 0,
    transition: 'transform 150ms'
  })
}));
const Dropdown = forwardRef(function Dropdown({
  id,
  overlay,
  children,
  disabled,
  onClickAway = () => {},
  defaultOpen = false,
  closeOnBlur = true,
  hideDropdownArrow = false,
  mobileBreakpoint = 'MOBILE',
  classes: classesProp,
  className: classNameProp,
  hiddenOverlayOnClose,
  closeOverlayOnEnter = true,
  'data-pp-at-target': targetId,
  alignment = 'left',
  dropdownDirection = 'bottom',
  showOutlineWhenFocused = true,
  lockFocus = true,
  title,
  'aria-label': ariaLabel,
  ...rest
}, triggerRefProp) {
  const isMobile = useMediaBreakpoint(mobileBreakpoint);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const close = () => {
    setIsOpen(false);
  };

  const classes = useStyles(classesProp, {
    disabled
  });
  const className = clsx(classes.root, classNameProp);

  const clickAwayAndClose = () => {
    if (isMobile) return;
    if (isOpen) onClickAway();

    if (closeOnBlur) {
      close();
    }
  };

  if (disabled && isOpen) {
    onClickAway();
    close();
  }

  const ref = useRef(null);
  useOnClickOutside([ref], clickAwayAndClose, isOpen);
  useLockBodyScroll(isMobile && isOpen);
  const containerRef = useRef();
  useEffect(() => {
    if (isMobile) {
      containerRef.current = document.createElement('div');
      containerRef.current.setAttribute('data-pp-at-target', `${targetId}-container`);
      document.body.appendChild(containerRef.current);
    }

    return () => {
      if (isMobile && containerRef.current) document.body.removeChild(containerRef.current);
    };
  }, [isMobile, targetId]);
  const [bindTriggerRef, triggerRef] = useForwardedRef(triggerRefProp);

  const eatAndSetVisible = (event, newVisible) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(newVisible);

    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const handleKeyDown = event => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case 'Escape':
        eatAndSetVisible(event, false);
        break;

      case 'Enter':
        if (isOpen && !closeOverlayOnEnter) {
          break;
        }

        eatAndSetVisible(event, !isOpen);
        break;

      case 'ArrowDown':
      case 'ArrowUp':
        if (event.altKey) {
          eatAndSetVisible(event, !isOpen);
        }

        break;

      case ' ':
        if (!isOpen) {
          eatAndSetVisible(event, !isOpen);
          event.preventDefault();
          event.stopPropagation();
        }

        break;

      default:
        break;
    }
  };

  const onClickDropdown = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!disabled) {
      if (isOpen) {
        clickAwayAndClose();
      } else {
        setIsOpen(true);
      }
    }
  };

  const getDropdownArrow = () => {
    if (hideDropdownArrow) {
      return null;
    }

    if (dropdownDirection === 'right') {
      return /*#__PURE__*/React.createElement(ArrowSingleIcon, {
        "data-pp-at-target": `${targetId}-icon`,
        direction: isOpen ? 'left' : 'right',
        classes: {
          root: classes.arrow
        }
      });
    }

    return /*#__PURE__*/React.createElement(ArrowSolidIcon, {
      "data-pp-at-target": `${targetId}-icon`,
      mode: isOpen ? 'up' : 'down',
      classes: {
        root: classes.arrow
      }
    });
  };

  const renderOverlay = () => {
    if (!isOpen && !hiddenOverlayOnClose) {
      return null;
    }

    if (isMobile && containerRef.current) {
      return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(FocusLock, {
        disabled: !lockFocus,
        returnFocus: true,
        autoFocus: true,
        className: classes.mobileOverlay
      }, overlay(close)), containerRef.current);
    }

    return /*#__PURE__*/React.createElement(Flyout, {
      "data-pp-at-target": `${targetId}-content-flyout`,
      className: classes.overlay,
      onKeyDown: handleKeyDown,
      isOpen: isOpen,
      preferredFlyoutDirection: dropdownDirection,
      preferredFlyoutAlignment: alignment // Dropdown should not "drop upwards"
      ,
      disableResponsiveFlyoutDirection: true,
      lockFocus: lockFocus
    }, overlay(close));
  };

  return /*#__PURE__*/React.createElement("div", Object.assign({
    ref: ref,
    className: className,
    "data-pp-at-target": targetId
  }, rest), /*#__PURE__*/React.createElement("div", {
    id: id,
    ref: bindTriggerRef,
    role: "button",
    tabIndex: 0,
    className: clsx(classes.summary, isOpen && classes.summaryActive, showOutlineWhenFocused && classes.showOutline),
    onKeyDown: handleKeyDown,
    onClick: onClickDropdown,
    "data-pp-at-target": `${targetId}-summary`,
    "aria-label": typeof ariaLabel === 'function' ? ariaLabel(isOpen) : ariaLabel,
    title: title
  }, typeof children === 'function' ? children(isOpen) : children, getDropdownArrow()), renderOverlay());
});
export default Dropdown;